/** Settings sheet (start screen and session). Tapping the version 5x within 3 s toggles debug mode. */
import { EYE_HEIGHT_PRESETS, type EyeHeightPreset, type Settings } from '../contracts';
import { APP_VERSION_LABEL, debugStore, settingsStore } from '../settings';
import { type Control, section, segmented, slider, toggle } from './controls';
import { button, h, setHidden } from './dom';
import { formatSignedDeg, TapCounter } from './format';
import { icon } from './icons';
import { Sheet } from './sheet';

export const SOURCES_TEXT =
  'Uferzonen: © Kanton Luzern (rawi), Open-By · Seeumriss © swisstopo · Karte: Esri, HERE, Garmin, © OpenStreetMap contributors, and the GIS user community';
export const DISCLAIMER = 'Orientierungshilfe, keine amtliche Navigationsgrundlage.';

const EYE_OPTIONS: { value: EyeHeightPreset; label: string }[] = [
  { value: 'kayak', label: 'Kajak/SUP' },
  { value: 'seated', label: 'Boot sitzend' },
  { value: 'standing', label: 'Boot stehend' },
  { value: 'yacht', label: 'Yacht' },
  { value: 'custom', label: 'Frei' },
];

const fmtM = (v: number) => `${v.toFixed(1)} m`;

export interface SettingsDeps {
  toast(text: string): void;
  openDebug(): void;
}

export class SettingsPanel {
  readonly sheet: Sheet;
  private readonly controls: Control[] = [];
  private readonly taps = new TapCounter(5, 3000);
  private readonly unsubs: (() => void)[] = [];

  constructor(deps: SettingsDeps) {
    this.sheet = new Sheet('Einstellungen');
    const s = () => settingsStore.get();
    const set = (patch: Partial<Settings>) => settingsStore.update(patch);

    // Augenhöhe
    const eyeValue = h('span', 'uz-sec-value');
    const eye = segmented<EyeHeightPreset>({
      label: 'Augenhöhe',
      columns: 2,
      options: EYE_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
        sub: o.value === 'custom' ? '0.3–5 m' : fmtM(EYE_HEIGHT_PRESETS[o.value]),
      })),
      get: () => s().eyeHeightPreset,
      set: (v) => set({ eyeHeightPreset: v, eyeHeightM: v === 'custom' ? s().eyeHeightM : EYE_HEIGHT_PRESETS[v] }),
    });
    eye.el.classList.add('uz-seg--eye');
    const eyeSlider = slider({
      label: 'Freie Augenhöhe',
      min: 0.3,
      max: 5,
      step: 0.1,
      get: () => s().eyeHeightM,
      set: (v) => set({ eyeHeightPreset: 'custom', eyeHeightM: Math.round(v * 10) / 10 }),
      format: fmtM,
      scale: ['0.3 m', '5 m'],
    });
    const eyeSync: Control = {
      el: eyeValue,
      sync: () => {
        eyeValue.textContent = fmtM(s().eyeHeightM);
        setHidden(eyeSlider.el, s().eyeHeightPreset !== 'custom');
      },
    };

    // GPS threshold
    const gps = slider({
      label: 'GPS-Schwelle',
      min: 3,
      max: 20,
      step: 1,
      get: () => s().gpsThresholdM,
      set: (v) => set({ gpsThresholdM: v }),
      format: (v) => `${v} m`,
      scale: ['3 m', '20 m'],
    });

    // Opacity
    const opacity = slider({
      label: 'Deckkraft',
      min: 0.2,
      max: 1,
      step: 0.05,
      get: () => s().overlayOpacity,
      set: (v) => set({ overlayOpacity: Math.round(v * 100) / 100 }),
      format: (v) => `${Math.round(v * 100)} %`,
      scale: ['20 %', '100 %'],
    });

    // View distance
    const viewDistance = slider({
      label: 'Sichtweite',
      min: 500,
      max: 5000,
      step: 250,
      get: () => s().viewDistanceM,
      set: (v) => set({ viewDistanceM: v }),
      format: (v) => `${(v / 1000).toFixed(1)} km`,
      scale: ['0.5 km', '5 km'],
    });

    // Declination
    const declValue = h('span', 'uz-sec-value');
    const decl = segmented<Settings['declinationMode']>({
      label: 'Missweisung',
      options: [
        { value: 'auto', label: 'Auto' },
        { value: 'manual', label: 'Manuell' },
      ],
      get: () => s().declinationMode,
      set: (v) => set({ declinationMode: v }),
    });
    const declSlider = slider({
      label: 'Missweisung manuell',
      min: -5,
      max: 10,
      step: 0.1,
      get: () => s().declinationDeg,
      set: (v) => set({ declinationDeg: Math.round(v * 10) / 10 }),
      format: (v) => `${formatSignedDeg(v)} ${v >= 0 ? 'Ost' : 'West'}`,
      scale: ['5° West', '10° Ost'],
    });
    const declHelp = h('p', 'uz-help');
    const declSync: Control = {
      el: declValue,
      sync: () => {
        const manual = s().declinationMode === 'manual';
        setHidden(declSlider.el, !manual);
        declValue.textContent = manual ? declSlider.valueEl.textContent ?? '' : 'Auto';
        declHelp.textContent = manual
          ? 'Magnetische Missweisung, Ost positiv.'
          : 'Automatisch: +3.6° Ost (Weltmagnetfeldmodell, Zentralschweiz 2026).';
      },
    };

    // Minimap
    const mini = toggle({
      label: 'Mini-Karte',
      sub: 'Kleine Karte unten links, antippen für Vollbild',
      get: () => s().showMinimap,
      set: (v) => set({ showMinimap: v }),
    });

    // Version + debug entry
    const versionBtn = button('uz-version', [h('span', null, null, ['Version']), h('b', null, null, [APP_VERSION_LABEL])], () => {
      if (!this.taps.tap(performance.now())) return;
      const enabled = !debugStore.get().enabled;
      debugStore.update({ enabled });
      deps.toast(enabled ? 'Debug-Modus aktiviert' : 'Debug-Modus deaktiviert');
    });
    versionBtn.setAttribute('aria-label', `Version ${APP_VERSION_LABEL}`);
    const debugLink = button('uz-linkrow', [h('span', null, null, ['Debug-Einstellungen']), icon('chevron')], () => deps.openDebug());
    const debugSync: Control = { el: debugLink, sync: () => setHidden(debugLink, !debugStore.get().enabled) };

    this.controls.push(eye, eyeSlider, eyeSync, gps, opacity, viewDistance, decl, declSlider, declSync, mini, debugSync);

    this.sheet.body.append(
      section('Augenhöhe', [eye.el, eyeSlider.el], { value: eyeValue, help: 'Höhe der Kamera über dem Wasser.' }),
      section('GPS-Schwelle', [gps.el], {
        value: gps.valueEl,
        help: 'Das Overlay erscheint erst, wenn das GPS mindestens so genau ist.',
      }),
      section('Deckkraft', [opacity.el], { value: opacity.valueEl, help: 'Deckkraft der Uferzonen im Kamerabild.' }),
      section('Sichtweite', [viewDistance.el], {
        value: viewDistance.valueEl,
        help: 'Wie weit die Uferzonen-Overlays sichtbar sind, bevor sie ausblenden.',
      }),
      section('Missweisung', [decl.el, declHelp, declSlider.el], { value: declValue }),
      section('Anzeige', [mini.el]),
      section('Info & Quellen', [
        h('div', 'uz-info', null, [
          h('p', 'uz-disclaimer', null, [icon('info'), h('span', null, null, [DISCLAIMER])]),
          h('p', null, null, ['Amtliche Uferzonen (innere, äussere, erweiterte) des Kantons Luzern: Luzerner Anteil von Vierwaldstätter-, Sempacher-, Zuger- und Hallwilersee.']),
          h('p', 'uz-sources', null, [SOURCES_TEXT]),
        ]),
      ]),
      section('App', [versionBtn, debugLink]),
    );

    const syncAll = () => this.controls.forEach((c) => c.sync());
    this.unsubs.push(settingsStore.subscribe(syncAll), debugStore.subscribe(() => debugSync.sync()));
    syncAll();
  }

  dispose(): void {
    this.unsubs.forEach((u) => u());
  }

  open(opener?: HTMLElement | null): void {
    this.taps.reset();
    this.sheet.open(opener);
  }
}
